/**
 * PKCE (Proof Key for Code Exchange) utilities — RFC 7636
 *
 * Used by auth flows that redirect to Keycloak with code_challenge.
 */

const PKCE_SESSION_KEY_PREFIX = 'guardrail_pkce_';

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const value of bytes) {
    binary += String.fromCharCode(value);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/** Generate a cryptographically random base64url string. */
export function randomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** SHA-256 hash a string and return it base64url-encoded. */
export async function sha256Base64Url(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Generate PKCE params, store the verifier in sessionStorage keyed by state,
 * and append state / code_challenge / code_challenge_method to the given URL.
 *
 * @param loginUrl - Base Keycloak authorization URL (from /v2/auth/login-url)
 * @returns The URL with PKCE params appended
 */
export async function appendPkce(loginUrl: string): Promise<string> {
  const state = randomString(16);
  const codeVerifier = randomString(64);
  const codeChallenge = await sha256Base64Url(codeVerifier);

  sessionStorage.setItem(`${PKCE_SESSION_KEY_PREFIX}${state}`, codeVerifier);

  const url = new URL(loginUrl);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');

  return url.toString();
}

export { PKCE_SESSION_KEY_PREFIX };
